import { IProductDTO } from '@legacy/shared-types/dto/product.type';
import { Alert, App, Flex, Spin } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { btlProductApi } from '@api/product/btlProductApi';
import { CaseSidebarItemId } from '@components/CaseSidebar/types';
import { isOoProductEditableStatus } from '@components/CaseSidebar/config/ooStatus';
import { FormValidationContext, useOnNext } from '@hooks/useBtlFormValidation';
import { useBtlApplicationInProgressById } from '@queries/btlApplications/useApplicationInProgressById/useBtlApplicationInProgressById';
import { useBtlProductByAppId } from '@queries/products/useBtlProductByAppId';
import { TypedFormatMessageFn } from '@app-types/intl';
import type { OoFormRouteContext } from '../types';
import { useOoFormMappedRoutes } from '../useOoFormMappedRoutes';
import { OoProductItem } from './OoProductItem';

interface OoProductSelectorProps {
  routeContext: OoFormRouteContext;
}

export function OoProductSelector({ routeContext }: OoProductSelectorProps) {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState<IProductDTO>();
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const caseId = routeContext.caseId;
  const { data, isLoading } = useBtlApplicationInProgressById(caseId);
  const isProductEditable = isOoProductEditableStatus(
    data?.status,
    routeContext.sidebarContext.canEditDipAcceptedSections,
  );
  const { data: products, isLoading: isProductsLoading } = useBtlProductByAppId(caseId);
  const { nextSection } = useOoFormMappedRoutes(routeContext);

  const handleNavigate = (itemId: CaseSidebarItemId) => {
    navigate({ search: `?section=${itemId}` });
  };

  const formValidationContext = useContext(FormValidationContext);
  if (!formValidationContext) {
    throw new Error('OoProductSelector must be used within a FormValidationProvider');
  }
  const { setIsValid, setIsSubmitting } = formValidationContext;

  useEffect(() => {
    setIsValid(!isProductEditable || Boolean(selectedProduct));
  }, [isProductEditable, selectedProduct, setIsValid]);

  useEffect(() => {
    setSelectedProduct(data?.product);
  }, [data]);

  useOnNext(async () => {
    if (!isProductEditable || !selectedProduct) {
      return;
    }

    setIsSubmitting(true);
    try {
      await btlProductApi.updateBtlProduct(caseId, selectedProduct.id);
      if (nextSection) {
        handleNavigate(nextSection);
      }
    } catch (error: unknown) {
      console.error(error);
      message.error(error instanceof Error ? error.message : 'Failed to update product');
    } finally {
      setIsSubmitting(false);
    }
  });

  if (isLoading || isProductsLoading) {
    return <Spin size={'large'} />;
  }

  const activeProducts = products?.filter(product => product.isActive);
  const sortedProducts = activeProducts?.sort(
    (productA, productB) =>
      productA.monthsInitial - productB.monthsInitial || productA.maximumFTV - productB.maximumFTV,
  );

  return (
    <Flex gap={16} wrap align='stretch'>
      {sortedProducts?.length ? (
        (selectedProduct && !data?.isCurrentProductSuitable && selectedProduct?.id === data?.product?.id
          ? [
              <OoProductItem
                key={'current-product'}
                product={selectedProduct}
                isSelected={true}
                isSelectable={isProductEditable}
                style={{ opacity: 0.5 }}
                invalidated
              />,
            ]
          : []
        ).concat(
          sortedProducts.map(product => (
            <OoProductItem
              key={product.id}
              product={product}
              isSelected={selectedProduct?.id === product.id}
              onProductSelected={nextProduct => setSelectedProduct(nextProduct)}
              isSelectable={isProductEditable}
            />
          )),
        )
      ) : (
        <Alert
          description={formatMessage({ id: 'ooApplicationForm.PRODUCT.noProducts.message' })}
          message={formatMessage({ id: 'ooApplicationForm.PRODUCT.noProducts.title' })}
          type='warning'
          showIcon
        />
      )}
    </Flex>
  );
}
